import React from 'react';
import { motion } from 'framer-motion';
import {
  FiArrowRight,
  FiImage,
  FiMail,
  FiPenTool,
  FiShare2,
  FiShoppingBag,
  FiTarget,
  FiTrendingUp,
  FiVideo,
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const applicationUrl = 'https://go.digitalgigs.eu/joindigitalgigs';

const roles = [
  {
    icon: FiPenTool,
    title: 'Blog Writer',
    description:
      'Create useful, engaging, SEO-aware blog articles and website content for businesses across different industries.',
    skills: ['SEO writing', 'Research', 'Clear communication'],
  },
  {
    icon: FiShare2,
    title: 'Social Media Manager',
    description:
      'Plan content, manage social accounts, and help businesses stay consistent and engaging across social platforms.',
    skills: ['Content planning', 'Social media', 'Community engagement'],
  },
  {
    icon: FiTarget,
    title: 'Lead Generation Specialist',
    description:
      'Help businesses attract qualified prospects through outreach, campaign support, landing pages, and conversion-focused systems.',
    skills: ['Lead research', 'Outreach', 'Marketing strategy'],
  },
  {
    icon: FiVideo,
    title: 'Video Editor / AI Video Creator',
    description:
      'Create short-form videos, advert creatives, and branded content for campaigns and social media.',
    skills: ['Video editing', 'Short-form content', 'Creative storytelling'],
  },
  {
    icon: FiMail,
    title: 'Email Marketing Expert',
    description:
      'Create email campaigns, automations, newsletters, and conversion-focused sequences that help businesses nurture leads.',
    skills: ['Email automation', 'Copywriting', 'Campaign strategy'],
  },
  {
    icon: FiImage,
    title: 'Graphic Designer',
    description:
      'Design professional graphics and branded visual content for websites, social media, advertising campaigns, and digital products.',
    skills: ['Brand design', 'Social graphics', 'Marketing visuals'],
  },
  {
    icon: FiShoppingBag,
    title: 'Shopify Expert',
    description:
      'Build, customise, and optimise Shopify stores with polished storefronts, product pages, apps, and conversion-focused experiences.',
    skills: ['Shopify setup', 'Store customisation', 'E-commerce optimisation'],
  },
  {
    icon: FiTrendingUp,
    title: 'TikTok Shop Expert',
    description:
      'Help brands launch and grow on TikTok Shop through product listings, content strategy, creator collaborations, and sales optimisation.',
    skills: ['TikTok Shop', 'Product promotion', 'Social commerce'],
  },
  {
    icon: FiTarget,
    title: 'Digital Products Creator & Sales Expert',
    description:
      'Create, package, market, and sell digital products such as ebooks, templates, courses, guides, and downloadable resources.',
    skills: ['Digital product creation', 'Online sales', 'Launch strategy'],
  },
];

const Opportunities = () => {
  return (
    <section id="opportunities" className="min-h-screen bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-20 max-w-3xl text-center">
          <p className="mb-4 text-sm font-black uppercase tracking-[0.2em] text-emerald-600">
            Join DigitalGigs
          </p>

          <h1 className="mb-6 text-4xl font-extrabold tracking-tight text-slate-900 md:text-6xl">
            Help Us Build the Next Generation of Digital Growth
          </h1>

          <p className="text-xl font-medium leading-relaxed text-slate-600">
            Explore the roles we are currently looking for. Select a role to continue
            to the DigitalGigs application page.
          </p>
        </div>

        <div className="mb-16 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="mb-3 text-sm font-black uppercase tracking-[0.2em] text-emerald-600">
              Current openings
            </p>

            <h2 className="text-3xl font-extrabold text-slate-900 md:text-4xl">
              Roles We Are Looking For
            </h2>
          </div>

          <p className="max-w-md font-medium text-slate-500">
            Find the role that best suits your skills, then apply through our dedicated
            application page.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {roles.map((role, index) => (
            <motion.article
              key={role.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className="group flex flex-col rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:border-emerald-200 hover:shadow-xl"
            >
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 transition-colors group-hover:bg-emerald-600 group-hover:text-white">
                <SafeIcon icon={role.icon} className="text-2xl" />
              </div>

              <h3 className="mb-3 text-2xl font-bold text-slate-900">{role.title}</h3>

              <p className="mb-6 flex-grow font-medium leading-relaxed text-slate-600">
                {role.description}
              </p>

              <div className="mb-8 flex flex-wrap gap-2">
                {role.skills.map((skill) => (
                  <span
                    key={skill}
                    className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600"
                  >
                    {skill}
                  </span>
                ))}
              </div>

              <a
                href={applicationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center font-black text-emerald-600 transition-colors hover:text-emerald-700"
              >
                Apply for this role
                <SafeIcon
                  icon={FiArrowRight}
                  className="ml-2 transition-transform group-hover:translate-x-1"
                />
              </a>
            </motion.article>
          ))}
        </div>

        <div className="mt-16 rounded-[2.5rem] bg-slate-900 px-8 py-10 text-center shadow-xl md:px-14">
          <p className="mb-3 text-sm font-black uppercase tracking-[0.2em] text-emerald-400">
            Ready to contribute?
          </p>

          <h2 className="mb-6 text-3xl font-extrabold text-white md:text-4xl">
            Think You Would Be a Great Fit?
          </h2>

          <a
            href={applicationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-2xl bg-emerald-400 px-8 py-4 font-bold text-slate-950 transition hover:bg-emerald-300"
          >
            View Application Form
            <SafeIcon icon={FiArrowRight} className="ml-3" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Opportunities;
